import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { BackendTypes, CSTypes } from '@tf/api';
import { CSClient } from '@tf/api';
import constate from 'constate';
import type React from 'react';

import { getClientAreaIrQuery, getProcessingScriptsQuery, getTenantConfigQuery } from '@/core/api';
import { QueryLoader } from '@/core/providers/QueryLoader';

import { useConfig } from './ConfigProvider';
import { useCurrentLocale } from './I18nProvider';
import { useOriginTenantSlug } from './OriginTenantSlugProvider';

type State = {
	revision: string;
	currency: string;
	defs: CSTypes.GetClientAreaIrResponse;
	scripts: Record<BackendTypes.SegmentKind, string[]>;
	reloadDefinitions: () => Promise<any>;
};

const [DefinitionsStateProvider, useDefinitionsState] = constate((props: { value: State }) => {
	return props.value;
});

export const useDefinitions = useDefinitionsState;

export const DefinitionsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const config = useConfig();
	const tenant = useOriginTenantSlug();
	const currentLocale = useCurrentLocale();

	const definitionsQuery = useQuery({
		queryKey: ['definitions', currentLocale],
		queryFn: async () => {
			CSClient.init(config.urls.cs);

			const tenantConfig = await getTenantConfigQuery(tenant);
			const [defs, scripts] = await Promise.all([
				getClientAreaIrQuery(tenantConfig.revision, currentLocale),
				getProcessingScriptsQuery(tenantConfig.revision),
			]);

			defs.forms.sort((a, b) => a.order - b.order);

			return { revision: tenantConfig.revision, currency: tenantConfig.currency, defs, scripts };
		},
		placeholderData: keepPreviousData,
		staleTime: Infinity,
	});

	return (
		<QueryLoader
			query={definitionsQuery}
			errorMessage="Failed to load definitions"
			renderData={(data) => {
				return (
					<DefinitionsStateProvider value={{ ...data, reloadDefinitions: definitionsQuery.refetch }}>
						{children}
					</DefinitionsStateProvider>
				);
			}}
		/>
	);
};
